import { Form } from "react-bootstrap";
import { RegisterFormModel } from "../../../types/Register/RegisterFormModel";
import { getIn, useFormikContext } from "formik";
import useSsoService from "../../../hooks/useSsoService";
import { useEffect, useState } from "react";
import { Country } from "../../../types/Country/Country";
import { Option } from "react-bootstrap-typeahead/types/types";
import FormikTypeahead from "../../../@ui/FormikTypeahead/FormikTypeahead";
import { useCurrencyContext } from "../../../context/Currency/CurrencyContext";
import CountryTypeahead from "../../CountryTypeahead/CountryTypeahead";
import { useTenantContext } from "../../../context/Tenant/TenantContext";

const OrganisationDetailsForm = () => {
  const { handleChange, values, touched, errors, handleBlur, setFieldValue } =
    useFormikContext<RegisterFormModel>();

  const { currencies } = useCurrencyContext();
  const ssoService = useSsoService();
  const [countryOptions, setCountryOptions] = useState<Country[]>([]);
  const [currencyOptions, setCurrencyOptions] = useState<Option[]>([]);
  const { config } = useTenantContext();

  const isInrix = config.name === "Inrix";

  useEffect(() => {
    ssoService.GetCountries().then((countries: Country[]) => {
      setCountryOptions(countries);
    });
  }, []);

  useEffect(() => {
    setCurrencyOptions(
      currencies?.map((currency) => ({
        label: `(${currency.code}) ${currency.name} ${currency.symbol}`,
        value: currency.code,
      })) ?? []
    );
  }, [currencies]);

  useEffect(() => {
    var countryOption = getIn(values, "organisationDetails.address.countryId");

    var country = countryOptions?.find((c) => c.id === countryOption);

    if (country) {
      setFieldValue("organisationDetails.country", country, true);

      setFieldValue(
        "organisationDetails.currencyCode",
        country.defaultCurrencyCode
      );

      setFieldValue("organisationDetails.phone", country.callingCode);
    }
  }, [values.organisationDetails.address.countryId]);

  return (
    <>
      {countryOptions && (
        <>
          <Form.Group className="mb-3">
            <Form.Label className="mb-0">Name</Form.Label>
            <Form.Control
              type="input"
              onChange={handleChange}
              value={values.organisationDetails.name}
              isInvalid={
                touched.organisationDetails?.name &&
                !!errors.organisationDetails?.name
              }
              onBlur={handleBlur}
              name="organisationDetails.name"
            />
            <Form.Control.Feedback type="invalid">
              {errors.organisationDetails?.name}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="mb-0">Email</Form.Label>
            <Form.Control
              onChange={handleChange}
              value={values.organisationDetails.billingEmail}
              isInvalid={
                touched.organisationDetails?.billingEmail &&
                !!errors.organisationDetails?.billingEmail
              }
              onBlur={handleBlur}
              name="organisationDetails.billingEmail"
            />
            <Form.Control.Feedback type="invalid">
              {errors.organisationDetails?.billingEmail}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="mb-0">First Line of Address</Form.Label>
            <Form.Control
              type="input"
              onChange={handleChange}
              value={values.organisationDetails.address.firstLine}
              isInvalid={
                touched.organisationDetails?.address?.firstLine &&
                !!errors.organisationDetails?.address?.firstLine
              }
              onBlur={handleBlur}
              name="organisationDetails.address.firstLine"
            />
            <Form.Control.Feedback type="invalid">
              {errors.organisationDetails?.address?.firstLine}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="mb-0">Second Line of Address</Form.Label>
            <Form.Control
              type="input"
              onChange={handleChange}
              value={values.organisationDetails.address.secondLine}
              isInvalid={
                touched.organisationDetails?.address?.secondLine &&
                !!errors.organisationDetails?.address?.secondLine
              }
              onBlur={handleBlur}
              name="organisationDetails.address.secondLine"
            />
            <Form.Control.Feedback type="invalid">
              {errors.organisationDetails?.address?.secondLine}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="mb-0">City</Form.Label>
            <Form.Control
              type="input"
              onChange={handleChange}
              value={values.organisationDetails.address.city}
              isInvalid={
                touched.organisationDetails?.address?.city &&
                !!errors.organisationDetails?.address?.city
              }
              onBlur={handleBlur}
              name="organisationDetails.address.city"
            />
            <Form.Control.Feedback type="invalid">
              {errors.organisationDetails?.address?.city}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="mb-0">State</Form.Label>
            <Form.Control
              type="input"
              onChange={handleChange}
              value={values.organisationDetails.address.state}
              isInvalid={
                touched.organisationDetails?.address?.state &&
                !!errors.organisationDetails?.address?.state
              }
              onBlur={handleBlur}
              name="organisationDetails.address.state"
            />
            <Form.Control.Feedback type="invalid">
              {errors.organisationDetails?.address?.state}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="mb-0">
              Postcode {isInrix ? "/ Zip Code" : ""}
            </Form.Label>
            <Form.Control
              type="input"
              onChange={handleChange}
              value={values.organisationDetails.address.postCode}
              isInvalid={
                touched.organisationDetails?.address?.postCode &&
                !!errors.organisationDetails?.address?.postCode
              }
              onBlur={handleBlur}
              name="organisationDetails.address.postCode"
            />
            <Form.Control.Feedback type="invalid">
              {errors.organisationDetails?.address?.postCode}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="mb-0">Country</Form.Label>
            <CountryTypeahead
              name={"organisationDetails.address.countryId"}
              options={countryOptions.map((country) => ({
                label: country.name,
                value: country.id,
                code: country.countryCode,
              }))}
              isInvalid={
                touched.organisationDetails?.address?.countryId &&
                !!errors.organisationDetails?.address?.countryId
              }
              onBlur={handleBlur}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="mb-0">Currency</Form.Label>
            <FormikTypeahead
              name={"organisationDetails.currencyCode"}
              options={currencyOptions}
              isInvalid={
                touched.organisationDetails?.currencyCode &&
                !!errors.organisationDetails?.currencyCode
              }
              onBlur={handleBlur}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="mb-0">Phone number</Form.Label>
            <Form.Control
              type="input"
              onChange={handleChange}
              value={values.organisationDetails.phone}
              isInvalid={
                touched.organisationDetails?.phone &&
                !!errors.organisationDetails?.phone
              }
              onBlur={handleBlur}
              name="organisationDetails.phone"
            />
            <Form.Control.Feedback type="invalid">
              {errors.organisationDetails?.phone}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="mb-0">
              VAT Number {isInrix ? "(Optional)" : ""}
            </Form.Label>
            <Form.Control
              onChange={handleChange}
              value={values.organisationDetails.vatNumber}
              isInvalid={
                touched.organisationDetails?.vatNumber &&
                !!errors.organisationDetails?.vatNumber
              }
              onBlur={handleBlur}
              name="organisationDetails.vatNumber"
            />
            <Form.Control.Feedback type="invalid">
              {errors.organisationDetails?.vatNumber}
            </Form.Control.Feedback>
          </Form.Group>
        </>
      )}
    </>
  );
};

export default OrganisationDetailsForm;
